import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { withStyles } from '@material-ui/styles';
import { lighten, makeStyles } from '@material-ui/core/styles';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import StopSharpIcon from '@material-ui/icons/StopSharp';
import PlayCircleFilledWhiteOutlinedIcon from '@material-ui/icons/PlayCircleFilledWhiteOutlined';
import EditIcon from '@material-ui/icons/Edit';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import GetAppIcon from '@material-ui/icons/GetApp';
import Skeleton from '@material-ui/lab/Skeleton';
import RefreshIcon from '@material-ui/icons/Refresh';

import CurrentStatus from 'components/atoms/CurrentStatus';
import ToolTipWrap from 'components/molecules/ToolTipWrap';
import SearchInput from 'components/molecules/SearchInput';

const useToolbarStyles = makeStyles((theme) => ({
  tableToolbarArea: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: 35,
    padding: '5px 10px',
    '& > div': {
      margin: '0 10px',
      '&:first-child': { marginLeft: 0 },
      '&:last-child': { margin: 0, marginLeft: 'auto' },
    },
  },
  highlight:
    theme.palette.type === 'light'
      ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark,
        },
  title: { lineHeight: '35px' },
  actions: {
    color: theme.palette.text.secondary,
    display: 'flex',
    marginLeft: 'auto',
  },
  iconBtn: { padding: 7 },
  controllerIcon: { width: 35, height: 35 },
}));

const EnhancedTableToolbar = (props) => {
  const { t } = useTranslation();
  const classes = useToolbarStyles();
  const {
    numSelected,
    title,
    columnSetButton,
    useExcel,
    screenMode,
    handleExcelDonload,
    handleScreenMode,
    handleSearch,
    useSearch,
    toolTip,
    isFrame,
    hasActController,
    headCallBack = (f) => f, // Head에서 사용하는 버튼 callBack함수
    getParam,
    loadingExcel = false,
    checkList,
    loading,
    params,
    useFullScreen,
    useReFetchBtn,
    handleFetch,
  } = props;

  /**
   * 체크박스 Btn Callback 함수
   * @param type String
   * @param rowData Array
   */
  const handleHeadBtn = (type) => {
    headCallBack(type, checkList);
  };

  return (
    <Toolbar
      className={clsx(classes.tableToolbarArea, {
        [classes.highlight]: numSelected > 0,
      })}
    >
      {loading ? (
        <Skeleton style={{ width: '100%' }} />
      ) : (
        <>
          {/* 툴팁 추가시 toolTip에 추가해주시면 됩니다. 여러줄 필요하실땐 변수에 담아서 <p>태그와 <br/>태그를 사용하여 추가해주세요. */}
          {toolTip !== null && toolTip !== undefined && (
            <>
              <ToolTipWrap
                className="tableFilter"
                title={toolTip}
                icon="faInfoCircle"
                iconColor="#c5c6d0"
              />
            </>
          )}
          {title !== '' && <div>{title}</div>}
          {/* 컬럼설정, 복사 등등 */}
          {columnSetButton}
          {/* 검색 */}
          {useSearch && (
            <SearchInput
              placeholder={t('검색어를 입력하세요.')}
              handleSearch={handleSearch}
              params={params}
              getParam={getParam}
            />
          )}
          {numSelected > 0 ? (
            <div>
              <Typography
                className={classes.title}
                color="inherit"
                variant="subtitle1"
              >
                {numSelected} {t('선택')}
              </Typography>
            </div>
          ) : (
            <div>
              <Typography
                className={classes.title}
                id="tableTitle"
                variant="subtitle1"
              />
            </div>
          )}
          <div className={classes.actions}>
            {numSelected > 0 ? (
              <>
                {hasActController.start === true ? (
                  <ToolTipWrap title={t('게재')}>
                    <IconButton
                      aria-label={t('게재')}
                      onClick={(e) => handleHeadBtn('start')}
                      className={classes.controllerIcon}
                    >
                      <PlayCircleFilledWhiteOutlinedIcon />
                    </IconButton>
                  </ToolTipWrap>
                ) : null}
                {hasActController.stop === true ? (
                  <ToolTipWrap title={t('중지')}>
                    <IconButton
                      aria-label={t('중지')}
                      onClick={(e) => handleHeadBtn('stop')}
                      className={classes.controllerIcon}
                    >
                      <StopSharpIcon />
                    </IconButton>
                  </ToolTipWrap>
                ) : null}
                {hasActController.reject === true ? (
                  <ToolTipWrap title={t('반려')}>
                    <IconButton
                      aria-label={t('반려')}
                      onClick={(e) => handleHeadBtn('reject')}
                      className={classes.controllerIcon}
                    >
                      <StopSharpIcon />
                    </IconButton>
                  </ToolTipWrap>
                ) : null}
                {hasActController.waiting === true ? (
                  <ToolTipWrap title={t('대기')}>
                    <IconButton
                      aria-label={t('대기')}
                      onClick={(e) => handleHeadBtn('waiting')}
                      className={classes.controllerIcon}
                    >
                      <PlayCircleFilledWhiteOutlinedIcon />
                    </IconButton>
                  </ToolTipWrap>
                ) : null}
                {hasActController.edit === true ? (
                  <ToolTipWrap title={t('수정')}>
                    <IconButton
                      aria-label={t('수정')}
                      onClick={(e) => handleHeadBtn('edit')}
                      className={classes.controllerIcon}
                      fontSize="small"
                    >
                      <EditIcon />
                    </IconButton>
                  </ToolTipWrap>
                ) : null}
                {hasActController.delete === true ? (
                  <ToolTipWrap title={t('삭제')}>
                    <IconButton
                      aria-label={t('삭제')}
                      onClick={(e) => handleHeadBtn('delete')}
                      className={classes.controllerIcon}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ToolTipWrap>
                ) : null}
              </>
            ) : (
              <>
                {useReFetchBtn && (
                  <ToolTipWrap title={t('새로고침')}>
                    <IconButton
                      aria-label={t('새로고침')}
                      onClick={(e) => handleFetch()}
                      className={classes.iconBtn}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </ToolTipWrap>
                )}
                {useExcel && (
                  <ToolTipWrap title={t('엑셀 다운로드')}>
                    <IconButton
                      aria-label={t('엑셀 다운로드')}
                      onClick={loadingExcel ? (f) => f : handleExcelDonload}
                      className={classes.iconBtn}
                    >
                      {loadingExcel ? (
                        <CurrentStatus type="excel" />
                      ) : (
                        <GetAppIcon />
                      )}
                    </IconButton>
                  </ToolTipWrap>
                )}
                {useFullScreen && !isFrame && (
                  <IconButton
                    aria-label={
                      screenMode === '' ? t('전체 화면') : t('부분 화면')
                    }
                    onClick={handleScreenMode}
                    className={classes.iconBtn}
                  >
                    {screenMode === '' ? (
                      <FullscreenIcon />
                    ) : (
                      <FullscreenExitIcon />
                    )}
                  </IconButton>
                )}
              </>
            )}
          </div>
        </>
      )}
    </Toolbar>
  );
};

const useStyles = (theme) => ({
  root: {
    width: '100%',
    borderTop: theme.palette.box.border,
  },
  columnSet: (props) => {
    const { headcells } = props;
    const result = {};

    if (typeof headcells === 'undefined') {
      return null;
    }
    for (let headCell of headcells.values()) {
      const { id, className, displayFlag } = headCell;
      if (id === 'SEQ') {
        continue;
      }
      if (!displayFlag) {
        result[`& .${className}`] = {
          display: 'none',
        };
      }
    }
    return result;
  },
});

export default withStyles(useStyles)(
  class EnhancedTableWrap extends React.Component {
    render() {
      const {
        toolTip,
        columnSetButton,
        selectedCnt,
        title = '',
        useExcel,
        useSearch,
        handleExcelDonload,
        handleScreenMode,
        screenMode,
        handleSearch,
        isFrame,
        hasActController,
        headCallBack = (f) => f, // Head에서 사용하는 버튼 callBack함수
        loadingExcel,
        className,
        children,
        classes,
        checkList,
        loading,
        params,
        useFullScreen,
        handleFetch,
        useReFetchBtn,
        useToolBar,
        getParam = (f) => f,
        ...rest
      } = this.props;

      return useToolBar ? (
        <div
          className={clsx(classes.root, classes.columnSet, className)}
          {...rest}
        >
          <EnhancedTableToolbar
            numSelected={selectedCnt}
            title={title}
            columnSetButton={columnSetButton}
            useExcel={useExcel}
            screenMode={screenMode}
            handleExcelDonload={handleExcelDonload}
            handleScreenMode={handleScreenMode}
            handleSearch={handleSearch}
            useSearch={useSearch}
            toolTip={toolTip}
            isFrame={isFrame}
            hasActController={hasActController}
            headCallBack={headCallBack}
            loadingExcel={loadingExcel}
            checkList={checkList}
            loading={loading}
            params={params}
            useFullScreen={useFullScreen}
            useReFetchBtn={useReFetchBtn}
            handleFetch={handleFetch}
            getParam={getParam}
          />
          {children}
        </div>
      ) : (
        children
      );
    }
  }
);
